/**
 * Team Notifications Utility
 * Send notifications to team members when points are scored
 */

import { supabase } from "@/lib/supabase/client";
import { createInAppNotification, scheduleLocalNotification } from "@/lib/notifications";

interface NotifyTeamOfPointsParams {
  earnerId: string;
  earnerName: string;
  teamId: string;
  points: number;
  gameName: string;
  reason: string;
  weekendId?: string;
  sessionId?: string;
  isWin?: boolean;
}

/**
 * Notify all team members when a teammate earns points
 */
export const notifyTeamOfPoints = async ({
  earnerId,
  earnerName,
  teamId,
  points,
  gameName,
  reason,
  weekendId,
  sessionId,
  isWin = false,
}: NotifyTeamOfPointsParams): Promise<void> => {
  try {
    // Get all team members except the earner
    const { data: teamMembers, error: membersError } = await supabase
      .from("team_members")
      .select(`
        user_id,
        user:profiles!team_members_user_id_fkey(id, full_name, notification_preferences)
      `)
      .eq("team_id", teamId)
      .neq("user_id", earnerId);

    if (membersError) {
      console.error("Error fetching team members:", membersError);
      return;
    }

    if (!teamMembers || teamMembers.length === 0) {
      return;
    }

    // Determine notification content
    const isPositive = points > 0;
    const absPoints = Math.abs(points);
    
    const title = isPositive
      ? isWin
        ? `🏆 ${earnerName} heeft gewonnen!`
        : `🎉 ${earnerName} scoorde ${absPoints} punten!`
      : `${earnerName} verloor ${absPoints} punten`;

    const body = isPositive
      ? `${gameName}: ${reason}`
      : `${gameName}: ${reason}`;

    const lottieAnimation = isPositive ? "confetti" : undefined;

    // Send notification to each team member
    for (const member of teamMembers) {
      // Handle joined data from Supabase
      const userData = member.user;
      const user = userData && typeof userData === 'object' && !Array.isArray(userData)
        ? userData as { 
            id: string; 
            full_name: string; 
            notification_preferences: { 
              categories?: { score_updates?: boolean; team_updates?: boolean } 
            } | null 
          }
        : null;

      // Check if user has score/team notifications enabled
      const prefs = user?.notification_preferences;
      const scoreUpdatesEnabled = prefs?.categories?.score_updates !== false;
      const teamUpdatesEnabled = prefs?.categories?.team_updates !== false;

      if (!scoreUpdatesEnabled && !teamUpdatesEnabled) {
        continue;
      }

      // Create in-app notification
      await createInAppNotification({
        userId: member.user_id,
        title,
        body,
        type: isPositive ? "teammate_scored" : "teammate_lost_points",
        actionUrl: `/scores/team/${teamId}`,
        actionData: {
          team_id: teamId,
          session_id: sessionId,
          points,
        },
        senderId: earnerId,
        weekendId,
        lottieAnimation,
      });

      // Get push token and send push notification
      const { data: pushTokens } = await supabase
        .from("push_tokens")
        .select("token")
        .eq("user_id", member.user_id)
        .eq("is_active", true);

      if (pushTokens && pushTokens.length > 0 && isPositive) {
        // Only send push for positive points
        await scheduleLocalNotification({
          title: `Team Update! ${isWin ? "🏆" : "🎉"}`,
          body: `${earnerName} scoorde ${absPoints} punten met ${gameName}`,
          data: {
            type: "teammate_scored",
            team_id: teamId,
            session_id: sessionId,
          },
          lottieAnimation,
        });
      }
    }
  } catch (error) {
    console.error("Error notifying team of points:", error);
  }
};

/**
 * Notify team members of game start
 */
export const notifyTeamOfGameStart = async ({
  starterId: _starterId,
  starterName,
  teamId,
  gameName,
  gameSlug,
  sessionId,
}: {
  starterId: string;
  starterName: string;
  teamId: string;
  gameName: string;
  gameSlug: string;
  sessionId: string;
}): Promise<void> => {
  try {
    const { data: teamMembers } = await supabase
      .from("team_members")
      .select("user_id")
      .eq("team_id", teamId);

    if (!teamMembers || teamMembers.length === 0) return;

    for (const member of teamMembers) {
      await createInAppNotification({
        userId: member.user_id,
        title: `🎮 ${starterName} speelt ${gameName}!`,
        body: "Kijk mee of moedig aan!",
        type: "team_game_started",
        actionUrl: `/games/${gameSlug}/spectate/${sessionId}`,
        actionData: { session_id: sessionId },
      });
    }
  } catch (error) {
    console.error("Error notifying team of game start:", error);
  }
};

/**
 * Notify ALL weekend participants when someone scores points (silent, in-app only)
 */
export const notifyAllUsersOfScore = async ({
  earnerId,
  earnerName,
  teamName,
  points,
  gameName,
  weekendId,
  isNewRecord = false,
}: {
  earnerId: string;
  earnerName: string;
  teamName: string;
  points: number;
  gameName: string;
  weekendId: string;
  isNewRecord?: boolean;
}): Promise<void> => {
  try {
    // Get all users in the weekend (through team_members)
    const { data: allMembers, error } = await supabase
      .from("team_members")
      .select(`
        user_id,
        teams!inner(weekend_id)
      `)
      .eq("teams.weekend_id", weekendId);

    if (error || !allMembers) {
      console.error("Error fetching weekend members:", error);
      return;
    }

    // Get unique user IDs, excluding the earner
    const userIds = [...new Set(allMembers.map((m: any) => m.user_id))].filter(
      (id) => id !== earnerId
    );

    if (userIds.length === 0) return;

    // Create notification content
    const title = isNewRecord
      ? `🏆 Nieuw Familie Record!`
      : `🎮 ${earnerName} scoorde!`;
    
    const body = isNewRecord
      ? `${earnerName} (${teamName}) heeft het familie record gebroken en ${points} punten verdiend met ${gameName}!`
      : `${earnerName} (${teamName}) verdiende ${points} punten met ${gameName}`;

    const lottieAnimation = isNewRecord ? "trophy" : "confetti";

    // Create in-app notifications (no push - silent)
    await Promise.all(
      userIds.map((userId) =>
        createInAppNotification({
          userId,
          title,
          body,
          type: "game_score_update",
          actionUrl: "/(tabs)/teams",
          actionData: {
            game_name: gameName,
            points,
            earner_id: earnerId,
          },
          senderId: earnerId,
          weekendId,
          lottieAnimation,
        })
      )
    );
  } catch (error) {
    console.error("Error notifying all users of score:", error);
  }
};

/**
 * Send rematch notification
 */
export const sendRematchNotification = async ({
  fromUserId,
  fromUserName,
  toUserId,
  gameName,
  gameSlug,
  previousSessionId,
}: {
  fromUserId: string;
  fromUserName: string;
  toUserId: string;
  gameName: string;
  gameSlug: string;
  previousSessionId: string;
}): Promise<void> => {
  try {
    await createInAppNotification({
      userId: toUserId,
      title: `🔄 Rematch gevraagd!`,
      body: `${fromUserName} wil nog een keer ${gameName} spelen!`,
      type: "game_rematch",
      actionUrl: `/games/uitdagingen`,
      actionData: {
        game_slug: gameSlug,
        from_user_id: fromUserId,
        previous_session_id: previousSessionId,
      },
      senderId: fromUserId,
      lottieAnimation: "party",
    });

    // Send push notification
    const { data: pushTokens } = await supabase
      .from("push_tokens")
      .select("token")
      .eq("user_id", toUserId)
      .eq("is_active", true);

    if (pushTokens && pushTokens.length > 0) {
      await scheduleLocalNotification({
        title: `🔄 Rematch!`,
        body: `${fromUserName} wil nog een keer ${gameName} spelen!`,
        data: {
          type: "game_rematch",
          game_slug: gameSlug,
          from_user_id: fromUserId,
        },
      });
    }
  } catch (error) {
    console.error("Error sending rematch notification:", error);
  }
};

